import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { CloudUploadIcon, PhotographIcon } from "@heroicons/react/outline";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import type { IMediaSearch } from "../state/gallerySlice";
import { fetchFilteredMedias, getMediaSearch, setMediaSearch } from "../state/gallerySlice";
import ModalGallery from "src/features/graph/components/ModalGallery";
import type { IMedia } from "src/model/unityObject";

const BlockadeImageToImage = ({
  onSelect,
  onCancel,
  imageKind = "360",
}: {
  onSelect: (value: File | string) => void;
  onCancel: () => void;
  imageKind?: "360" | "2d";
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentSearch = useAppSelector(getMediaSearch) as IMediaSearch;
  // Snapshot of the launching gallery's filter, restored when the picker closes.
  const previousSearchRef = useRef<IMediaSearch | null>(null);
  // Hidden file input driven by the "Importer" card (same pattern as Nav.tsx).
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Restrict the picker to the images this mode expects: media360 for the 360°
  // img2img flow, media2d for the 2D-photo→360 flow. ModalGallery's tag gate is
  // a positive match, so we drive it (and the grid filter) with the exact tag.
  const requiredTag = imageKind === "360" ? "media360" : "media2d";

  const openGalleryPicker = () => {
    // This picker shares state.media with the gallery it is launched from, so
    // capture that filter first and restore it on close — otherwise the page
    // behind the picker stays stranded on our image/2D filter.
    previousSearchRef.current = currentSearch;
    dispatch(setMediaSearch({ mediaType: "image", media360tag: requiredTag, query: "", page: 0 }));
    dispatch(openStandardModal("modal-gallery"));
  };

  const closeGalleryPicker = () => {
    dispatch(closeStandardModal("modal-gallery"));
    if (previousSearchRef.current) {
      dispatch(setMediaSearch(previousSearchRef.current));
      void dispatch(fetchFilteredMedias({ limit: previousSearchRef.current.limit ?? 20 }));
      previousSearchRef.current = null;
    }
  };

  const handleGallerySelect = (media: IMedia) => {
    setPreview(media.s3_url);
    onSelect(media.s3_url);
    closeGalleryPicker();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = [".png", ".jpg", ".jpeg"];
      const fileName = file.name.toLowerCase();
      const isValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

      if (isValidExtension) {
        const fileUrl = URL.createObjectURL(file);
        setPreview(fileUrl);
        onSelect(file);
      } else {
        alert(t("general.extensionNotAcceptedImage"));
        handleCancel();
      }
    }
  };

  const handleCancel = () => {
    setPreview(null);
    onCancel();
  };

  // Upload launched from INSIDE the picker ("Importer un média" button or a
  // drag-and-drop on the grid). Same contract as the "Importer" card below: the
  // raw file becomes the base image directly, no gallery write — the DB-backed
  // upload slide-over can't be used here, it shares the global slide-over slot
  // with the generate panel hosting this flow and would unmount it.
  const handlePickerFileUpload = (file: File) => {
    setPreview(URL.createObjectURL(file));
    onSelect(file);
    closeGalleryPicker();
  };

  return (
    <div>
      {preview ? (
        <div className="relative">
          <Tooltip title={t("general.cancel")}>
            <button
              onClick={handleCancel}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full shadow-md"
              aria-label={t("pages.gallery.generateMediaPanel.closePreview")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-5 h-5 text-gray-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </Tooltip>
          <img
            src={preview}
            alt={t("pages.gallery.generateMediaPanel.previewImageAlt")}
            className="mb-6 w-full max-h-64 object-cover"
          />
        </div>
      ) : (
        // Same "on the nose" card style as the 2D/360 type chooser above.
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            onClick={openGalleryPicker}
            className="btn-primary-outline flex flex-col items-center gap-1 py-5"
          >
            <PhotographIcon className="w-8 h-8" />
            <span>{t("pages.gallery.generateMediaPanel.selectFromGallery")}</span>
            <span className="text-xs text-gray-500 font-normal">
              {t("pages.gallery.generateMediaPanel.selectFromGalleryHint")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-primary-outline flex flex-col items-center gap-1 py-5"
          >
            <CloudUploadIcon className="w-8 h-8" />
            <span>{t("pages.gallery.generateMediaPanel.uploadFile")}</span>
            <span className="text-xs text-gray-500 font-normal">
              {t("pages.gallery.generateMediaPanel.uploadFileHint")}
            </span>
          </button>
          <input
            ref={fileInputRef}
            id="file-upload"
            type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      )}
      {/* Separator only once a media is picked (it divides the preview from the
          influence/prompt section below). Hidden at the selection step, where it
          would dangle under the picker cards. */}
      {preview && <hr className="mt-6 mb-4 w-4/5 mx-auto border-gray-300" />}

      <ModalGallery
        nodeId="profile"
        onMediaChange={(media: IMedia) => handleGallerySelect(media)}
        onCancel={closeGalleryPicker}
        allowedMediaTypeOverride="image"
        allowedMedia360TagOverride={requiredTag}
        uploadAcceptOverride="image/jpeg,image/png,image/jpg"
        onUploadedFileSelect={handlePickerFileUpload}
      />
    </div>
  );
};

export default BlockadeImageToImage;
